'use client';

import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  type ReactNode,
} from 'react';
import type {
  Location as GeoLocation,
  SiteConfig,
} from '@hen-site/types/enrichedContent';
import type { MonarchBase } from '@hen-site/types/monarch';
import { defaultMonarchResponse } from '@hen-site/constants/monarch';
import type { PublisherTheme } from '@highereducation/publishers';

import type {
  CohesionErrorNormalized,
  CohesionPluginMonarchSlots,
  MonarchEvaluationsResult,
  MonarchEvaluationsRuleSet,
  MonarchExperience,
} from '@redventures/cohesion-utils-react';

/** Local types for preamp data (not exported in cohesion-utils-react v2) */
type CohesionPluginPreampPlacements = Record<string, unknown>;
type CohesionPluginPreampAssets = unknown[];

export type MonarchTrafficFlow = {
  err: CohesionErrorNormalized | null;
  result: MonarchEvaluationsResult | null;
  isLoading: boolean;
  rules: MonarchEvaluationsRuleSet;
  slots: CohesionPluginMonarchSlots;
  preampPlacements: CohesionPluginPreampPlacements;
  preampAssets: CohesionPluginPreampAssets;
};
interface GlobalProps {
  monarch: MonarchBase;
  monarchTrafficFlow?: MonarchTrafficFlow | null;
  domain?: string;
  template?: string;
  siteConfig?: Partial<SiteConfig>;
  id?: string;
  date?: string;
  authUrl?: string;
  isAuthenticated?: boolean;
  embeddedVoyagerActive?: boolean;
  embeddedVoyagerUrl?: string;
  theme?: PublisherTheme;
  location?: GeoLocation | null;
  [key: string]: unknown;
}
interface GlobalContextType extends GlobalProps {
  monarchTrafficFlow: MonarchTrafficFlow;
  monarch: MonarchBase;
  monarchExperience: MonarchExperience | null;
  updateGlobalProps: (updates: Partial<GlobalProps>) => void;
}

export interface GlobalContextProviderProps {
  children: ReactNode;
  monarchBase?: MonarchBase;
  monarchTrafficFlow?: MonarchTrafficFlow | null;
  globalProps: GlobalProps;
}

const defaultTrafficFlowResponse: MonarchTrafficFlow = {
  err: null,
  result: {} as MonarchEvaluationsResult,
  isLoading: true,
  rules: {},
  slots: {},
  preampPlacements: {},
  preampAssets: [],
};

const defaultGlobalContext: GlobalContextType = {
  monarch: defaultMonarchResponse,
  monarchTrafficFlow: defaultTrafficFlowResponse,
  monarchExperience: null,
  domain: '',
  template: '',
  siteConfig: {},
  id: '',
  date: '',
  authUrl: '',
  embeddedVoyagerActive: false,
  embeddedVoyagerUrl: '',
  location: null,
  updateGlobalProps: () => {},
};

const GlobalContext = createContext<GlobalContextType | undefined>(undefined);

const GlobalContextProvider: React.FC<GlobalContextProviderProps> = ({
  globalProps: initialProps,
  monarchBase,
  monarchTrafficFlow,
  children,
}) => {
  const [globalProps, setGlobalProps] = useState<GlobalProps>({
    ...initialProps,
  });

  // Experience returned from Monarch Traffic Flow
  const monarchReturnedExperience = useMemo(() => {
    const trafficFlow = monarchTrafficFlow?.result?.source
      ?.trafficFlow as unknown as
      | { returnDetails?: MonarchExperience }
      | undefined;
    return trafficFlow?.returnDetails as MonarchExperience | undefined;
  }, [monarchTrafficFlow]);

  const updateGlobalProps = useCallback((updates: Partial<GlobalProps>) => {
    setGlobalProps((prevProps) => ({
      ...prevProps,
      ...updates,
    }));
  }, []);

  return (
    <GlobalContext.Provider
      value={{
        ...globalProps,
        monarchTrafficFlow: monarchTrafficFlow || defaultTrafficFlowResponse,
        monarch: monarchBase || defaultMonarchResponse,
        monarchExperience: monarchReturnedExperience ?? null,
        updateGlobalProps,
      }}
    >
      {children}
    </GlobalContext.Provider>
  );
};

export const useGlobalProps = (): GlobalContextType => {
  const context = useContext(GlobalContext);
  if (!context) {
    throw new Error(
      'useGlobalProps must be used within a GlobalContextProvider',
    );
  }
  return context;
};

export default GlobalContextProvider;
